<template>
  <div class="form-group"
       ref="multiselect"
       :class="{ hasErrors }">
    <label class="form-label"
           :class="formLabelClass"
           v-if="label"
           :data-content="label">
      <span>{{ label }}</span>
      <span v-if="required"> *</span>
    </label>
    <div v-if="!editMode">
      <div v-if="!resultsBelow" class="word-break">{{ stringValue }}</div>
      <div v-if="resultsBelow" class="d-flex flex-wrap gap-2">
        <div class="service-multiselect-below-tag word-break"
             v-for="(o, oind) in belowTags"
             :key="`below-tag-${oind}`">
          {{ o[title] }}
        </div>
      </div>

    </div>
    <div v-if="editMode">
      <VueMultiselect :options="options"
                      :searchable="true"
                      v-model="f"
                      :class="{ textLikeInput, 'last-tag-locked': lastTagLocked }"
                      :label="title"
                      :placeholder="$t(placeholder)"
                      selectLabel=""
                      selectedLabel=""
                      deselectLabel=""
                      :internalSearch="internalSearch"
                      :track-by="title"
                      @remove="remove"
                      :allow-empty="allowEmpty"
                      :disabled="readonly"
                      @select="select"
                      :group-select="false"
                      :[groupLabel]="'category'"
                      :[groupValues]="'services'"
                      @open="serviceFind"
                      @search-change="serviceFind"
                      ref="multi"
                      :multiple="multiple">
<!--        <template #placeholder><div class="badge badge-info">asdfasdf</div></template>-->
        <template #selection v-if="resultsBelow"><span class="multiselect__placeholder">{{ belowTags.length ? $t(placeholder) : '' }}</span></template>
        <template #singleLabel>
          <div v-html="customSingleLabel ? customSingleLabel(f) : f[title]"/>
        </template>
        <template #option="{ option }"
                  v-if="customOptionsLabel">
          <span v-html="customOptionsLabel(option)"></span>
        </template>
        <template #option="{ option }"
                  v-else-if="$slots.option">
          <slot name="option" :option="option" />
        </template>
        <template #noResult>{{ $t('results_not_found') }}</template>
        <template #noOptions>{{ $t('results_not_found') }}</template>
        <template #afterList>
          <a class="text-center multiselect__element">
            <ScrollToEventElement v-if="canLoadMore && dbsearch && allowLoadOnScroll"
                                  @visible="loadOnScroll">
              &nbsp;
            </ScrollToEventElement>
          </a>
        </template>
      </VueMultiselect>
      <div class="d-flex flex-wrap gap-2 mt-2"
           v-if="resultsBelow && belowTags.length">
        <div class="service-multiselect-below-tag word-break"
             v-for="(o, oind) in belowTags"
             :key="`below-tag-${oind}`">
          {{ o[title] }}
          <a class="pointer font-size-0"
             v-if="!lastTagLocked"
             @click="removeBelow(o)">
            <CloseSvg class="svg-gray svg-size-sm"/>
          </a>
        </div>
      </div>
      <slot/>
      <ValidationError :class="[{ 'mt-1': !validationClass }, validationClass]"
                       v-if="showErrors"
                       :validationId="validationId"/>
    </div>
  </div>
</template>

<script setup>
import VueMultiselect from 'vue-multiselect'
import { EloquentService } from "@/services/api/eloquent.api.service"
import ValidationError from "@/components/form/ValidationError.vue"
import ScrollToEventElement from "@/components/parts/general/ScrollToEventElement.vue"
import CloseSvg from "@/assets/svg-vue/general/close.svg"
import {
  belowTags as buildBelowTags,
  clickedTagIndex,
  emittedValue,
  initialState,
  isLastTagLocked,
  selectionFromValue,
  selectionText,
  withoutIgnored,
  withoutTag
} from "@/services/multiSelect.helper"
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useStore } from "vuex"

// Used in ~110 places, mostly with v-model (`modelValue` prop + `update:modelValue` event).
// Contract (props, v-model, openDropdown) - see __tests__/form/serviceMultiSelectContract.test.js
const props = defineProps({
  modelValue: {},
  label: {},
  prepopulatedOptions: {},
  dbsearch: {
    type: Boolean,
    default: true
  },
  readonly: {
    type: Boolean,
    default: false
  },
  multiple: {
    type: Boolean,
    default: true
  },
  internalSearch: {
    type: Boolean,
    default: true
  },
  title: {
    type: String,
    default: 'name'
  },
  model: {
    type: String,
    default: 'service'
  },
  dblclick: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false
  },
  ignoreIds: {
    type: Array,
    default() {
      return []
    }
  },
  placeholder: {
    type: String,
    default: 'search'
  },
  onlyId: {
    type: Boolean,
    default: false
  },
  valueId: {
    type: String,
    default: 'id'
  },
  validationId: {},
  validationClass: {
    type: String,
    default: null
  },
  showErrors: {
    type: Boolean,
    default: false
  },
  customOptionsLabel: {
    type: Function,
    default: null
  },
  customSingleLabel: {
    type: Function,
    default: null
  },
  autocompleteAdditionalParams: {
    type: Object,
    default: null
  },
  allowLoadOnScroll: {
    type: Boolean,
    default: false
  },
  perPage: {
    type: Number,
    default: 24
  },
  editMode: {
    type: Boolean,
    default: true
  },
  textLikeInput: {
    type: Boolean,
    default: false
  },
  formLabelClass: {
    type: String,
  },
  resultsBelow: {
    type: Boolean,
    default: false
  },
  allowEmpty: {
    type: Boolean,
    default: true
  },
})

const emit = defineEmits(['select', 'remove', 'update:modelValue'])

const store = useStore()

const multiselect = ref(null)
const multi = ref(null)

const options = ref([])
// selection shown by vue-multiselect: option object, array of them (multiple) or null
const f = ref([])
const canLoadMore = ref(false)

// not rendered - not reactive
let searchString = null
let page = 1
let timer = null
let lastRequestId = 0
let openDropdownTimer = null

const errors = computed(() => store.state.datatable.validationErrors)
const superadmin = computed(() => store.state.auth.superadmin)
const clinic = computed(() => store.state.auth.clinic)
const branch = computed(() => store.state.auth.branch)

const groupLabel = computed(() => props.model === 'service' ? 'group-label' : '')
const groupValues = computed(() => props.model === 'service' ? 'group-values' : '')

const hasErrors = computed(() => {
  if(!props.showErrors || !props.validationId) return false
  return !!errors.value[props.validationId]
})

const stringValue = computed(() => selectionText(f.value, props.title))
const belowTags = computed(() => buildBelowTags(f.value, props.resultsBelow))
const lastTagLocked = computed(() => isLastTagLocked(f.value, props.allowEmpty))

const serviceFind = (query, loadingMore = false) => {
  searchString = query
  if(!props.dbsearch) return
  if(loadingMore) {
    page++
  } else {
    page = 1
  }
  if(timer) {
    clearTimeout(timer)
  }
  timer = setTimeout(() => {
    timer = null
    canLoadMore.value = false
    // only the latest request may update options: an earlier one can finish later (fast typing, load more)
    const requestId = ++lastRequestId
    EloquentService.autocomplete(props.model,
        query,
        props.perPage,
        true,
        superadmin.value,
        superadmin.value && clinic.value ? clinic.value.id : null,
        branch.value,
        props.autocompleteAdditionalParams,
        page
    ).then(res => {
      if(requestId !== lastRequestId) return
      const set = withoutIgnored(res.data.result, props.ignoreIds)
      if(loadingMore) {
        options.value = [...options.value, ...set]
      } else {
        options.value = set
      }
      canLoadMore.value = set.length === props.perPage
    })
  }, 270)
}

const tagClick = (e) => {
  const index = clickedTagIndex(e.target)
  if(index === -1) return
  f.value.push(f.value[index])
  emit('select', f.value[index])
}

const remove = (e) => {
  emit('remove', e)
}

const select = (e) => {
  emit('select', e)
}

const openDropdown = () => {
  openDropdownTimer = setTimeout(() => {
    openDropdownTimer = null
    multi.value?.activate()
  }, 100)
}

const loadOnScroll = () => {
  serviceFind(searchString, true)
}

const removeBelow = (option) => {
  if(!props.allowEmpty && belowTags.value.length <= 1) return
  f.value = withoutTag(f.value, option, props.title)
  emit('remove', option)
}

// deep: 1 - also in-place changes of a multiple selection (tagClick pushes into it)
watch(f, (val) => {
  // onlyId: a raw id (option not loaded) is not emitted back - see emittedValue
  const { emit: shouldEmit, value } = emittedValue(val, { onlyId: props.onlyId, valueId: props.valueId })
  if(shouldEmit) emit('update:modelValue', value)
}, { deep: 1 })

watch(() => props.modelValue, (val) => {
  f.value = selectionFromValue(val, options.value, { onlyId: props.onlyId, valueId: props.valueId })
}, { deep: 1 })

onMounted(() => {
  // double click on a tag adds the same option once more (e.g. the same service twice)
  if(props.dblclick && multiselect.value) {
    multiselect.value.addEventListener('dblclick', tagClick)
  }
  const initial = initialState(props.modelValue, props.prepopulatedOptions, { onlyId: props.onlyId, valueId: props.valueId })
  f.value = initial.selection
  if(initial.options) options.value = initial.options
})

onBeforeUnmount(() => {
  if(props.dblclick && multiselect.value) {
    multiselect.value.removeEventListener('dblclick', tagClick)
  }
  if(timer) clearTimeout(timer)
  if(openDropdownTimer) clearTimeout(openDropdownTimer)
})

// used by parent components through ref (see __tests__/form/serviceMultiSelectContract.test.js)
defineExpose({
  openDropdown,
})
</script>

<style scoped lang="scss">
.service-multiselect-below-tag {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 4px 8px;
  gap: 8px;
  font-size: 90%;
}

.multiselect {
  &.last-tag-locked :deep(.multiselect__tag-icon) {
    display: none;
  }

  :deep(.multiselect__content-wrapper) {
    overflow-x: hidden;
  }

  :deep(.multiselect__content .multiselect__element .multiselect__option) {
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 20px;
    padding-top: 5px;
    padding-bottom: 5px;
  }
}
</style>
